<template>
  <survey-question-card>
    <survey-question-number :number="number" v-if="number" />
    <div class="flex-grow-1">
      <div style="font-size: 1.2em;">{{ question.text || '-' }} <span v-if="question.required" class="error--text font-weight-bold">*</span></div>

      <warning-alert
        v-if="validationErrors && validationErrors.length > 0"
        class="mt-3"
      >
        <div v-for="(validationError, valI) in validationErrors" :key="valI">{{ validationError }}</div>
      </warning-alert>

      <v-radio-group
        v-if="isRadioType()"
        v-model="internalValueRadio"
        :readonly="readonly"
      >
        <v-radio
          v-for="(answer, i) in questionAnswers"
          :key="'question-answer-' + i"
          :value="answer.id"
          :label="answer.text"
          class="mt-2"
          :readonly="readonly"
          :style="getStylesByAnswer(answer)"
        >
        </v-radio>
      </v-radio-group>

      <template v-if="isType(SurveyQuestionTypes.MULTIPLE_CHOICE)">
        <v-checkbox
          v-for="(answer, i) in questionAnswers"
          v-model="resultAnswer.surveyAnswerIds"
          :value="answer.id"
          multiple
          :key="'question-answer-' + i"
          :label="answer.text"
          hide-details
          :readonly="readonly"
          :style="getStylesByAnswer(answer)"
        >
        </v-checkbox>
      </template>

      <template v-if="isType(SurveyQuestionTypes.TEXT)">
        <template v-if="readonly">
          <text-quote v-if="resultAnswer.text" :text="resultAnswer.text" />
          <div v-else class="no-data-placeholder mt-5 mb-2">{{ trans('survey.notAnsweredByGuest') }}</div>
        </template>
        <v-textarea
          v-else
          v-model="resultAnswer.text"
          class="mt-3"
          outlined
          :readonly="readonly"
          :rules="textFieldRules"
        ></v-textarea>
      </template>
    </div>
  </survey-question-card>
</template>

<script>
import {LocaleUtils} from '../../utils/locale-utils';
import {SurveyQuestionTypes} from '@/lib/survey-question-types';
import SurveyQuestionCard from '@/components/survey/SurveyQuestionCard';
import SurveyQuestionNumber from '@/components/survey/SurveyQuestionNumber';
import {SurveyQuestionMixin} from '@/components/survey/SurveyQuestionMixin';
import WarningAlert from '@/components/WarningAlert';
import TextQuote from './TextQuote';

export default {
  name: 'SurveyResultAnswer',
  components: {TextQuote, WarningAlert, SurveyQuestionNumber, SurveyQuestionCard},
  mixins: [
    LocaleUtils,
    SurveyQuestionMixin
  ],
  props: {
    number: {
      type: Number,
    },
    question: {
      type: Object,
      required: true
    },
    resultAnswer: {
      type: Object,
      default () {
        return {
          surveyQuestionId: null,
          surveyAnswerIds: null,
          text: null
        }
      }
    },
    readonly: {
      type: Boolean,
      default: false
    },
    validationErrors: {
      default () {
        return [];
      }
    }
  },
  data () {
    return {
      internalValueRadio: null,
      textFieldMaxCharacters: 2000
    }
  },
  computed: {
    textFieldRules () {
      return [v => (v + '').length <= this.textFieldMaxCharacters || this.trans('validationErrors.maxCharacters', {maxCharacters: this.textFieldMaxCharacters})];
    }
  },
  watch: {
    resultAnswer: {
      immediate: true,
      handler () {
        if (this.resultAnswer) {
          if (!this.resultAnswer?.surveyQuestionId && this.question.id) {
            this.resultAnswer.surveyQuestionId = this.question.id;
          }

          if (this.isRadioType() && this.resultAnswer.surveyAnswerIds?.length > 0) {
            if (this.resultAnswer.surveyAnswerIds.length === 1) {
              this.internalValueRadio = this.resultAnswer.surveyAnswerIds[0];
            } else {
              console.warn(`Survey question ${this.question.id} of type radio cannot have more than one surveyAnswerIds`);
            }
          }

          if (this.isType(SurveyQuestionTypes.TEXT) && this.resultAnswer.surveyAnswerIds) {
            this.resultAnswer.surveyAnswerIds = null;
          }
        }
      }
    },
    internalValueRadio () {
      this.resultAnswer.surveyAnswerIds = this.internalValueRadio
        ? [this.internalValueRadio]
        : []
      ;
    }
  },
  methods: {
    getStylesByAnswer (answer) {
      let styles = {};

      if (this.readonly) {
        if (!this.resultAnswer?.surveyAnswerIds?.includes(answer.id)) {
          styles.opacity = 0.6;
        }
        styles.pointerEvents = 'none';
      }

      return styles;
    }
  }
}
</script>

<style scoped lang="scss">
.drag-handle {
  opacity: 0.3;
}

.question-answer-item:hover {
  .drag-handle {
    opacity: 1;
  }
}

.answer-text-quote {
  opacity: 0.8;
  font-size: 1.04em;

  .answer-text-quote-symbol {
    font-size: 1.6em;

    &:first-child { margin-right: 3px; }
    &:last-child { margin-left: 2px; }
  }
}

.no-data-placeholder {
  font-size: 0.95em;
  opacity: 0.6;
}
</style>
